export const COMMUNITY_MEMBER_LOCATION_CITY_MAX = 120;
export const COMMUNITY_MEMBER_LOCATION_REGION_MAX = 120;
/** Decimal places for map display (~11 km at 1 decimal). */
export const COMMUNITY_MEMBER_MAP_COORDINATE_PRECISION = 1;

export type CommunityMemberLocationInput = {
  showLocationOnMap?: boolean;
  locationCity?: string | null;
  locationRegion?: string | null;
};

export type NormalizedCommunityMemberLocation = {
  showLocationOnMap: boolean;
  locationCity: string | null;
  locationRegion: string | null;
  locationLatitude: number | null;
  locationLongitude: number | null;
};

export function normalizeCommunityMemberLocationText(
  value: string | null | undefined,
  max: number,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, max);
}

export function blurCommunityMemberMapCoordinate(value: number): number {
  const factor = 10 ** COMMUNITY_MEMBER_MAP_COORDINATE_PRECISION;
  return Math.round(value * factor) / factor;
}

export function buildCommunityMemberLocationLabel(params: {
  city: string | null;
  region: string | null;
}): string | null {
  const parts = [params.city, params.region].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

export function validateCommunityMemberLocationUpdate(
  input: CommunityMemberLocationInput,
):
  | { ok: true; normalized: NormalizedCommunityMemberLocation }
  | { ok: false; message: string } {
  const showLocationOnMap = Boolean(input.showLocationOnMap);
  const locationCity = normalizeCommunityMemberLocationText(
    input.locationCity,
    COMMUNITY_MEMBER_LOCATION_CITY_MAX,
  );
  const locationRegion = normalizeCommunityMemberLocationText(
    input.locationRegion,
    COMMUNITY_MEMBER_LOCATION_REGION_MAX,
  );

  if (!showLocationOnMap) {
    return {
      ok: true,
      normalized: {
        showLocationOnMap: false,
        locationCity: null,
        locationRegion: null,
        locationLatitude: null,
        locationLongitude: null,
      },
    };
  }

  if (!locationCity) {
    return { ok: false, message: 'City is required when sharing your location on the map' };
  }

  return {
    ok: true,
    normalized: {
      showLocationOnMap: true,
      locationCity,
      locationRegion,
      locationLatitude: null,
      locationLongitude: null,
    },
  };
}

export function mapCommunityMemberLocationForDirectory(row: {
  showLocationOnMap: boolean;
  locationCity: string | null;
  locationRegion: string | null;
  locationLatitude: number | null;
  locationLongitude: number | null;
}) {
  if (!row.showLocationOnMap) return null;

  const hasStoredPin = row.locationLatitude != null && row.locationLongitude != null;
  const latitude = hasStoredPin
    ? blurCommunityMemberMapCoordinate(row.locationLatitude as number)
    : null;
  const longitude = hasStoredPin
    ? blurCommunityMemberMapCoordinate(row.locationLongitude as number)
    : null;

  return {
    city: row.locationCity,
    region: row.locationRegion,
    latitude,
    longitude,
    label: buildCommunityMemberLocationLabel({
      city: row.locationCity,
      region: row.locationRegion,
    }),
    hasMapPin: latitude != null && longitude != null,
  };
}

export type CommunityMapRegionAggregate = {
  region: string;
  country: string;
  count: number;
};

export function aggregateCommunityMapRegions(
  members: Array<{ locationRegion?: string | null; locationCity?: string | null }>,
): CommunityMapRegionAggregate[] {
  const counts = new Map<string, CommunityMapRegionAggregate>();
  for (const member of members) {
    const region = member.locationRegion?.trim() || 'Unknown region';
    const country = region.includes(',')
      ? region.split(',').pop()?.trim() || region
      : region;
    const key = `${region}::${country}`;
    const current = counts.get(key) ?? { region, country, count: 0 };
    current.count += 1;
    counts.set(key, current);
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.region.localeCompare(b.region));
}
